// Programmatic SEO keyword landing pages for Canadian security industry
// Each entry becomes /seo/:slug with unique title, meta, H1 and content
import { cities as _cities } from "./cities";
import { services as _services } from "./services";

// ---- helpers ----
export const slugifyKeyword = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const slugify = slugifyKeyword;

// Priority services for combo keywords
const priorityServices = [
  { slug: "static-guards", name: "Static Guard", noun: "static guards" },
  { slug: "mobile-patrol", name: "Mobile Patrol", noun: "mobile patrol" },
  { slug: "event-security", name: "Event Security", noun: "event security" },
  { slug: "mining-security", name: "Mining Security", noun: "mining site security" },
  { slug: "executive-protection", name: "Executive Protection", noun: "executive protection" },
  { slug: "construction-security", name: "Construction Site Security", noun: "construction site security" },
  { slug: "retail-loss-prevention", name: "Retail Loss Prevention", noun: "retail loss prevention" },
  { slug: "corporate-security", name: "Corporate Security", noun: "corporate security" },
  { slug: "residential-security", name: "Residential Security", noun: "residential security" },
  { slug: "alarm-response", name: "Alarm Response", noun: "alarm response" },
];

const priorityCities = [
  "Montreal", "Quebec City", "Laval", "Gatineau", "Longueuil",
  "Sherbrooke", "Trois-Rivieres", "Saguenay", "Val-d'Or", "Sept-Iles",
];

const citySlugMap = {
  "Montreal": "montreal",
  "Quebec City": "quebec-city",
  "Laval": "laval",
  "Gatineau": "gatineau",
  "Longueuil": "longueuil",
  "Sherbrooke": "sherbrooke",
  "Trois-Rivieres": "trois-rivieres",
  "Saguenay": "saguenay",
  "Val-d'Or": "val-dor",
  "Sept-Iles": "sept-iles",
};

// ---- 1) Service × City combos (50 keywords) ----
const serviceCityCombos = priorityServices.slice(0, 5).flatMap((s) =>
  priorityCities.map((city) => ({
    slug: slugify(`${s.slug}-${city}`),
    keyword: `${s.name} in ${city}`,
    h1: `${s.name} in ${city}, Quebec`,
    title: `${s.name} Services in ${city} | BSP-Licensed | Quebec Security Services`,
    description: `Professional ${s.noun} services in ${city}, Quebec. BSP-licensed, bilingual FR/EN officers, 24/7 dispatch. Request a quote — one business day reply.`,
    category: "service-city",
    service_slug: s.slug,
    service_name: s.name,
    service_noun: s.noun,
    city_name: city,
    city_slug: citySlugMap[city],
    keywords: [
      `${s.noun} ${city}`,
      `${s.name.toLowerCase()} ${city} Quebec`,
      `${s.noun} services ${city}`,
      `hire ${s.noun} ${city}`,
      `24/7 ${s.noun} ${city}`,
      `bilingual ${s.noun} ${city}`,
      `${s.noun} company ${city}`,
      `licensed ${s.noun} ${city} Quebec`,
    ],
  }))
);

// ---- 2) Long-tail / intent keywords (~45) ----
const longtail = [
  {
    keyword: "24 Hour Security Guards Quebec",
    h1: "24 Hour Security Guards in Quebec",
    description: "24/7 licensed security guard coverage across Quebec — Montreal, Laval, Quebec City, Gatineau and every incorporated municipality. Same-day emergency dispatch. Bilingual FR/EN.",
    hook: "Continuous, uninterrupted security coverage — every hour of every day across Quebec.",
    category: "intent",
    intent: "24-hour-availability",
  },
  {
    keyword: "Armed Security Guards Quebec",
    h1: "Armed Security Guards in Quebec (BSP Category C)",
    description: "BSP Category C armed security guards in Quebec — bonded, insured, trained in evasive driving, close protection and cash-in-transit. Deployed across Montreal, Laval and Quebec City.",
    hook: "BSP Category C licensed, PAI-certified, deployed only where the risk profile requires armed presence.",
    category: "intent",
    intent: "armed",
  },
  {
    keyword: "Unarmed Security Guards Quebec",
    h1: "Unarmed Security Guards in Quebec (BSP Category A)",
    description: "BSP Category A unarmed security guards for corporate lobbies, construction sites, events, condos and retail across Quebec. Bilingual FR/EN.",
    hook: "Uniformed, disciplined, and highly visible protection — the everyday backbone of Quebec's security industry.",
    category: "intent",
    intent: "unarmed",
  },
  {
    keyword: "Hire Security Guard Montreal",
    h1: "Hire a Security Guard in Montreal",
    description: "Hire a BSP-licensed security guard in Montreal — same-day emergency deployment or scheduled long-term coverage. Bilingual FR/EN officers. Request a quote in one business day.",
    hook: "Whether you need one officer for tonight or a 40-guard footprint by end of week, we deliver in Montreal.",
    category: "intent",
    intent: "hire",
  },
  {
    keyword: "Security Guard Company Near Me Quebec",
    h1: "Security Guard Company Near You in Quebec",
    description: "A BSP-licensed Quebec security guard company with sub-45-minute deployment reach to every incorporated municipality in the province. 24/7 command centre.",
    hook: "Wherever you are in Quebec, we're less than 45 minutes from your site.",
    category: "intent",
    intent: "near-me",
  },
  {
    keyword: "Best Security Company Quebec",
    h1: "One of Quebec's Highest-Rated Security Guard Companies",
    description: "Quebec Security Services is a BSP-permitted, CNESST-compliant security firm with 1,240+ officers and 17 regions of coverage. Ranked by clients for training, response and transparency.",
    hook: "The security operating standard that Quebec's corporate, mining, and event operators demand.",
    category: "intent",
    intent: "best",
  },
  {
    keyword: "Licensed Security Guards Quebec",
    h1: "BSP-Licensed Security Guards in Quebec",
    description: "Every officer on our Quebec roster holds an active Bureau de la sécurité privée (BSP) license — verifiable on-demand through our client portal. CNESST compliant. Insurance-grade documentation.",
    hook: "License card verifiable in the first 60 seconds of any officer's shift.",
    category: "intent",
    intent: "licensed",
  },
  {
    keyword: "Bonded and Insured Security Quebec",
    h1: "Bonded and Insured Security Services in Quebec",
    description: "$10M commercial general liability, professional liability, CNESST workers' compensation. Certificate of insurance issued at contract execution and renewed annually.",
    hook: "Full insurance-grade coverage — additional insured endorsements available on request.",
    category: "intent",
    intent: "bonded",
  },
  {
    keyword: "Emergency Security Services Quebec",
    h1: "Emergency Security Deployment in Quebec",
    description: "Same-day emergency security guard deployment across Quebec — post-incident fire watch, workplace-violence standby, immediate mobile patrol. Sub-12 minute alarm response.",
    hook: "When the incident just happened, we're staffing within hours — not days.",
    category: "intent",
    intent: "emergency",
  },
  {
    keyword: "Same Day Security Guards Montreal",
    h1: "Same-Day Security Guards in Montreal",
    description: "Same-day BSP-licensed security guard staffing in Montreal from our rapid-response bench. Post-incident, court-order, workplace-violence, fire watch — deployed within hours.",
    hook: "Call us at 09:00. Officer on-post by 16:00. In Montreal, same-day is a real thing.",
    category: "intent",
    intent: "same-day",
  },
  {
    keyword: "Temporary Security Guards Quebec",
    h1: "Temporary Security Guard Staffing in Quebec",
    description: "Short-term, event-based, or transitional security guard staffing across Quebec — from one-day event coverage to 90-day project ramps. BSP-licensed, bilingual, insurer-ready.",
    hook: "Not every security need is permanent. We staff the ones that aren't.",
    category: "intent",
    intent: "temporary",
  },
  {
    keyword: "Night Security Guards Montreal",
    h1: "Overnight Security Guards in Montreal",
    description: "Overnight security guards in Montreal for construction sites, warehouses, retail, condos and industrial parks. Randomized patrol windows, LPR camera integration, GPS-verified rounds.",
    hook: "Between 22:00 and 06:00, when the streetlights thin and the risk profile shifts.",
    category: "intent",
    intent: "night",
  },
  {
    keyword: "Weekend Security Guards Quebec",
    h1: "Weekend Security Guard Coverage in Quebec",
    description: "Saturday, Sunday and holiday security guard coverage across Quebec — retail, event, construction, and residential. Same officer roster maintained on weekend shifts.",
    hook: "Weekends are when 60% of construction copper theft happens. Cover them properly.",
    category: "intent",
    intent: "weekend",
  },
  {
    keyword: "Female Security Guards Quebec",
    h1: "Female Security Guards in Quebec",
    description: "Female BSP-licensed security officers for events, healthcare, retail, luxury residential, and hospitality contexts across Quebec. Bilingual FR/EN.",
    hook: "The right officer for the right context — including gender-appropriate deployment on request.",
    category: "intent",
    intent: "female",
  },
  {
    keyword: "French Speaking Security Guards Quebec",
    h1: "French-Speaking Security Guards in Quebec",
    description: "Every officer on our Quebec roster is bilingual French/English to CEFR B2 minimum. Native francophone officers available on request for francophone-facing posts.",
    hook: "Vos locataires, employés et invités servis en français — sans exception.",
    category: "intent",
    intent: "french",
  },
  {
    keyword: "Bilingual Security Guards Montreal",
    h1: "Bilingual French/English Security Guards in Montreal",
    description: "Bilingual FR/EN security guards for Montreal corporate towers, condos, hotels, retail and events. CEFR B2 minimum roster-wide, CEFR C1 on tenant-facing posts.",
    hook: "Every Montreal building has both languages. Every officer of ours does too.",
    category: "intent",
    intent: "bilingual",
  },
  {
    keyword: "Plainclothes Security Quebec",
    h1: "Plainclothes Security Services in Quebec",
    description: "Plainclothes / undercover floor detectives for retail loss prevention, event VIP protection, and workplace threat assessment across Quebec.",
    hook: "Sometimes the officer you can't see is the one who catches the incident.",
    category: "intent",
    intent: "plainclothes",
  },
  {
    keyword: "Off Duty Police Security Quebec",
    h1: "Off-Duty Police & Ex-Law-Enforcement Security in Quebec",
    description: "Ex-Sûreté du Québec, ex-SPVM, ex-RCMP officers on our executive protection and high-risk deployment roster. Category C armed authorization where applicable.",
    hook: "When the deployment needs law-enforcement instincts — not just training.",
    category: "intent",
    intent: "off-duty-police",
  },
  {
    keyword: "Supplemental Security Officers Quebec",
    h1: "Supplemental Security Officers in Quebec",
    description: "Overflow, project-based, and roster-augmentation security officers for Quebec facilities managers, GCs, and event producers running an existing security program.",
    hook: "When your primary program needs help without swapping vendors.",
    category: "intent",
    intent: "supplemental",
  },
  {
    keyword: "School Security Guards Quebec",
    h1: "School & Campus Security Guards in Quebec",
    description: "K-12, CEGEP, and university security guards across Quebec — dorm access, dark-hours escort, lockdown drill facilitation, workplace-violence prevention.",
    hook: "Learning environments deserve dedicated security discipline — not repurposed retail officers.",
    category: "intent",
    intent: "school",
  },
  {
    keyword: "Airport Security Services Quebec",
    h1: "Airport & Aviation Security Services in Quebec",
    description: "Landside security, perimeter patrol, cargo apron support and executive-jet ground protection at YUL Montreal-Trudeau, YQB Quebec Jean-Lesage and regional airports.",
    hook: "Aviation-adjacent security — the discipline landside operators need but often can't source.",
    category: "intent",
    intent: "airport",
  },
  {
    keyword: "Shopping Mall Security Quebec",
    h1: "Shopping Mall Security in Quebec",
    description: "Shopping mall and commercial plaza security across Quebec — DIX30, Carrefour Laval, Galeries de la Capitale, Place Ste-Foy and independent centres. Uniformed presence + plainclothes LP.",
    hook: "Foot traffic, food-court chaos, ORC rings — mall security is a discipline of its own.",
    category: "intent",
    intent: "mall",
  },
  {
    keyword: "Condo Building Security Quebec",
    h1: "Condominium Building Security in Quebec",
    description: "Concierge, mobile patrol, alarm response and after-hours coverage for condominium towers in Montreal, Laval, Quebec City, Brossard and Griffintown.",
    hook: "The concierge desk your board committee will actually be proud of.",
    category: "intent",
    intent: "condo",
  },
  {
    keyword: "Apartment Building Security Montreal",
    h1: "Apartment Building Security in Montreal",
    description: "Overnight and 24/7 security officers for Montreal apartment buildings and multi-tenant residential — package management, guest access, tenant-conflict de-escalation.",
    hook: "For landlords who take tenant safety as seriously as tenant rent.",
    category: "intent",
    intent: "apartment",
  },
  {
    keyword: "Office Building Security Quebec",
    h1: "Office Building Security in Quebec",
    description: "Class-A office tower security in downtown Montreal, Laval, Quebec City, Gatineau — concierge, tenant liaison, badge issuance, workplace-violence prevention, evacuation coordination.",
    hook: "The tenant retention factor most landlords underestimate.",
    category: "intent",
    intent: "office",
  },
  {
    keyword: "Warehouse Security Guards Quebec",
    h1: "Warehouse & Distribution Centre Security in Quebec",
    description: "Distribution centre, cold storage, e-commerce fulfillment and rail-adjacent warehouse security across Boucherville, Anjou, Laval, Vaudreuil-Dorion and Quebec City industrial parks.",
    hook: "Cargo theft, dock supervision, trailer sealing — warehouse security is not lobby work.",
    category: "intent",
    intent: "warehouse",
  },
  {
    keyword: "Hospital Security Guards Quebec",
    h1: "Hospital & Healthcare Security in Quebec",
    description: "CIUSSS, CISSS, McGill University Health Centre, CHUM and private clinic security across Quebec. IAHSS-aligned officers, de-escalation, Section 21 restraint, ER support.",
    hook: "The healthcare security discipline your risk manager knows you actually need.",
    category: "intent",
    intent: "hospital",
  },
  {
    keyword: "Restaurant Security Quebec",
    h1: "Restaurant & Bar Security in Quebec",
    description: "Front-door, back-of-house and after-hours security for Quebec restaurants, cocktail bars and nightlife venues — Old Port, Plateau, Griffintown, Vieux-Québec.",
    hook: "The security officer your maître d' introduces by name.",
    category: "intent",
    intent: "restaurant",
  },
  {
    keyword: "Bar Nightclub Security Quebec",
    h1: "Bar & Nightclub Security in Quebec",
    description: "Door screening, ID verification, VIP escort and back-of-house coverage for Quebec bars, nightclubs and lounge venues — Crescent, Saint-Laurent, Grande-Allée.",
    hook: "Door discipline. Crowd flow. VIP handling. Trained for the room, not just the checklist.",
    category: "intent",
    intent: "nightclub",
  },
  {
    keyword: "Wedding Security Services Quebec",
    h1: "Wedding & Private Event Security in Quebec",
    description: "Discreet security officers for weddings, private galas, engagement parties and family celebrations across Quebec — Montreal, Charlevoix, Cantons-de-l'Est, Mont-Tremblant.",
    hook: "Discreet, tuxedo-appropriate, and completely invisible until they're needed.",
    category: "intent",
    intent: "wedding",
  },
  {
    keyword: "Trade Show Security Quebec",
    h1: "Trade Show & Conference Security in Quebec",
    description: "Palais des congrès, Centre des congrès de Québec, and boutique trade show security across Quebec — booth surveillance, badge verification, VIP escort, exhibitor equipment protection.",
    hook: "Between booth setup and teardown lies the equipment theft window we close.",
    category: "intent",
    intent: "trade-show",
  },
  {
    keyword: "Concert Security Quebec",
    h1: "Concert & Live Music Security in Quebec",
    description: "Concert security for Bell Centre exterior, MTelus, Videotron Centre and independent venues across Quebec — crowd management, magnetometer screening, artist compound.",
    hook: "From boutique 400-person venues to 60,000-seat festivals.",
    category: "intent",
    intent: "concert",
  },
  {
    keyword: "Sports Event Security Quebec",
    h1: "Sports & Stadium Event Security in Quebec",
    description: "Stadium-perimeter and sports event security in Quebec — Stade Saputo, Bell Centre exterior, Videotron Centre, IGA Stadium. Certified crowd management (IAVM/ESA).",
    hook: "The pre-game, in-game, and post-game security choreography that stadium operators actually need.",
    category: "intent",
    intent: "sports",
  },
  {
    keyword: "VIP Bodyguard Quebec",
    h1: "VIP Bodyguard & Close Protection in Quebec",
    description: "CPO-certified bodyguards for celebrities, executives, foreign dignitaries and high-net-worth families in Quebec. Advance route planning, secure driving, family escort.",
    hook: "Discretion is a training curriculum, not a personality trait.",
    category: "intent",
    intent: "bodyguard",
  },
  {
    keyword: "Data Centre Security Quebec",
    h1: "Data Centre & Server Farm Security in Quebec",
    description: "Physical security for data centres, colocation facilities, and edge compute sites across Quebec — biometric access control, escort protocols, camera analytics, Loi 25 compliance.",
    hook: "Physical + digital security convergence — where SOC 2 audit meets patrol officer.",
    category: "intent",
    intent: "data-centre",
  },
  {
    keyword: "Cannabis Security Quebec",
    h1: "Cannabis Retail & Production Security in Quebec",
    description: "SQDC-standard cannabis retail security plus licensed producer facility protection — access control, vault protocol, product-loss investigation, Loi 25 CCTV compliance.",
    hook: "Compliance-grade security for Quebec's most tightly regulated retail category.",
    category: "intent",
    intent: "cannabis",
  },
  {
    keyword: "Movie Theatre Security Quebec",
    h1: "Movie Theatre & Cinema Security in Quebec",
    description: "Cineplex-standard cinema security across Quebec — bag-check, seat disputes, lobby de-escalation, and after-hours perimeter for multi-screen venues.",
    hook: "The quiet, courteous discipline cinemas need but rarely staff correctly.",
    category: "intent",
    intent: "cinema",
  },
  {
    keyword: "Church Religious Building Security Quebec",
    h1: "Religious Building & Place of Worship Security in Quebec",
    description: "Church, synagogue, mosque and temple security across Quebec — services, high-holiday coverage, weddings, funerals, community events. Discreet, culturally trained officers.",
    hook: "Sacred spaces deserve security that respects the room.",
    category: "intent",
    intent: "religious",
  },
  {
    keyword: "Government Building Security Quebec",
    h1: "Government & Municipal Building Security in Quebec",
    description: "Cleared security officers for provincial, municipal and federal facilities in Quebec — courthouses, city halls, service centres, ministerial offices.",
    hook: "CGP/CSAT-screened officers for the buildings the public expects to be secure.",
    category: "intent",
    intent: "government",
  },
  {
    keyword: "Manufacturing Plant Security Quebec",
    h1: "Manufacturing Plant Security in Quebec",
    description: "Industrial manufacturing plant security across Quebec — gate control, driver check-in, TDG/WHMIS compliance, night-shift patrol, forklift certification audit.",
    hook: "The plant floor security your CNESST auditor will thank you for.",
    category: "intent",
    intent: "manufacturing",
  },
  {
    keyword: "Financial Institution Security Quebec",
    h1: "Bank & Financial Institution Security in Quebec",
    description: "Bank branch, credit union (Desjardins-adjacent), wealth-management office and back-office financial security across Quebec — armed and unarmed deployments.",
    hook: "The industry that first defined 'bonded and insured' — and still holds the standard.",
    category: "intent",
    intent: "financial",
  },
  {
    keyword: "Energy Sector Security Quebec",
    h1: "Energy Sector & Utility Security in Quebec",
    description: "Hydro-Québec substation, refinery, pipeline compressor station and LNG terminal security in Quebec — TDG/WHMIS, confined-space awareness, cold-region deployment.",
    hook: "Critical infrastructure demands critical-infrastructure discipline.",
    category: "intent",
    intent: "energy",
  },
  {
    keyword: "Logistics Distribution Security Quebec",
    h1: "Logistics & Distribution Security in Quebec",
    description: "Cross-dock, freight consolidation, rail-adjacent, and third-party logistics facility security across Quebec — dock supervision, seal verification, driver kiosk.",
    hook: "Chain-of-custody, guaranteed — from tractor to trailer to bay door.",
    category: "intent",
    intent: "logistics",
  },
  {
    keyword: "Cold Storage Facility Security Quebec",
    h1: "Cold Storage Facility Security in Quebec",
    description: "Frozen food, pharmaceutical, and vaccine cold-chain storage security in Quebec — temperature-log verification, escort protocol, break-glass response.",
    hook: "The Quebec cold-storage security niche most vendors won't touch.",
    category: "intent",
    intent: "cold-storage",
  },
  {
    keyword: "Telecommunications Facility Security Quebec",
    h1: "Telecommunications & Tower Site Security in Quebec",
    description: "Cell tower, fibre hub, central office and telecom-adjacent security across Quebec — copper theft prevention, restricted-area badging, ILM support.",
    hook: "Telecom sites — the theft target most companies underestimate.",
    category: "intent",
    intent: "telecom",
  },
  {
    keyword: "Diplomatic Consulate Security Quebec",
    h1: "Diplomatic Mission & Consulate Security in Quebec",
    description: "Consulate, trade mission, and international delegation security across Montreal and Quebec City — cleared officers, protocol-trained, multilingual.",
    hook: "The protocol-grade security foreign missions in Quebec quietly rely on.",
    category: "intent",
    intent: "diplomatic",
  },
  {
    keyword: "Cheap Security Guards Quebec",
    h1: "Affordable Security Guard Rates in Quebec",
    description: "Transparent per-hour Quebec security guard pricing — no surprise surcharges, no hidden overtime multipliers. Scope-based quotes typically 8–15% below broker-tiered pricing.",
    hook: "Not the cheapest. The clearest.",
    category: "intent",
    intent: "cheap",
  },
];

const intentPages = longtail.map((k) => ({
  slug: slugify(k.keyword),
  keyword: k.keyword,
  h1: k.h1,
  title: `${k.h1} | Quebec Security Services`,
  description: k.description,
  hook: k.hook,
  category: "intent",
  intent: k.intent,
  keywords: [k.keyword, k.keyword.toLowerCase(), `${k.keyword.toLowerCase()} services`, `${k.keyword.toLowerCase()} company`],
}));

// ---- 3) Industry vertical pages ----
const verticals = [
  { key: "cannabis-retail", h1: "Cannabis Retail Security in Quebec", desc: "SQDC-standard cannabis retail security, vault protocol, product-loss investigation, CCTV compliance." },
  { key: "cannabis-production", h1: "Cannabis Licensed Producer Security in Quebec", desc: "Health Canada-standard security for cannabis licensed producers — perimeter, vault, personnel access, escort protocol." },
  { key: "biotech-lab", h1: "Biotech & Life Sciences Lab Security in Quebec", desc: "Laval biotech corridor, Montreal life sciences, controlled-substance vault, contamination-zone escort." },
  { key: "insurance-claims", h1: "Insurance Claims Site Security in Quebec", desc: "Post-fire, post-flood, post-incident site preservation, evidence chain-of-custody, insurer-ready DAR." },
  { key: "art-gallery", h1: "Art Gallery & Museum Security in Quebec", desc: "MMFA, MNBAQ, boutique gallery and private collection security — art-handling protocol, opening-night crowd management." },
  { key: "auction-house", h1: "Auction House Security in Quebec", desc: "High-value auction preview, sale-day and shipment security — plainclothes floor coverage, cash-in-transit integration." },
  { key: "yacht-marina", h1: "Yacht & Marina Security in Quebec", desc: "St-Laurent marinas, Îles-de-la-Madeleine, private yacht slip protection, dockside patrol, dock-master liaison." },
  { key: "film-production", h1: "Film & TV Production Security in Quebec", desc: "Montreal film-industry production security — set perimeter, talent trailer, equipment truck, wrap-shift patrol." },
  { key: "wind-farm", h1: "Wind Farm & Renewable Energy Security in Quebec", desc: "Gaspésie, Côte-Nord wind farm perimeter, substation security, remote site patrol, cold-weather deployment." },
  { key: "railway-yard", h1: "Railway Yard & Rail-Adjacent Security in Quebec", desc: "CN, CP, VIA Rail-adjacent yard security in Quebec — trespass deterrence, copper theft prevention, hazmat rail-car escort." },
  { key: "religious-jewish", h1: "Synagogue & Jewish Community Security in Quebec", desc: "Culturally trained, discreetly armed where applicable, high-holiday coverage, community centre and school liaison." },
  { key: "religious-muslim", h1: "Mosque & Muslim Community Security in Quebec", desc: "Culturally trained officers for Friday prayer coverage, Ramadan events, community-centre and school security." },
  { key: "senior-living", h1: "Senior Living & Retirement Residence Security in Quebec", desc: "Overnight coverage, wander-response, family-liaison, medication-cart escort in Quebec RPA residences." },
  { key: "campground-park", h1: "Campground & Provincial Park Security in Quebec", desc: "SEPAQ-adjacent, private campground and RV park security — after-dark patrol, wildlife-aware, guest de-escalation." },
  { key: "sports-club", h1: "Country Club & Private Sports Club Security in Quebec", desc: "Discreet officers for private golf, tennis, and yacht clubs across Quebec — event coverage, member privacy protocol." },
];

const verticalPages = verticals.map((v) => ({
  slug: slugify(v.key),
  keyword: v.h1.replace(" in Quebec", ""),
  h1: v.h1,
  title: `${v.h1} | Quebec Security Services`,
  description: `${v.desc} BSP-licensed, bilingual FR/EN, insurer-ready.`,
  hook: v.desc,
  category: "vertical",
  keywords: [
    v.h1.toLowerCase(),
    `${v.key.replace(/-/g, " ")} security Quebec`,
    `${v.key.replace(/-/g, " ")} security Montreal`,
    `${v.key.replace(/-/g, " ")} security services`,
  ],
}));

// ---- 4) Auto-generated per-city keyword pages ----
// For every city, expand every keyword tag into its own SEO landing page.
const cityKeywordTemplates = (cityName) => [
  `security guard ${cityName}`,
  `security services ${cityName}`,
  `mobile patrol ${cityName}`,
  `event security ${cityName}`,
  `construction security ${cityName}`,
  `corporate security ${cityName}`,
  `residential security ${cityName}`,
  `mining security ${cityName}`,
  `24/7 security ${cityName}`,
  `bilingual security ${cityName}`,
  `BSP licensed security ${cityName}`,
  `agent de sécurité ${cityName}`,
  `security company near me ${cityName}`,
  `hire security ${cityName}`,
  `emergency security ${cityName}`,
  `${cityName} security`,
];

const autoCityPages = [];
const _seenAutoSlugs = new Set();
_cities.forEach((c) => {
  const keywords = [...(c.keywords || []), ...cityKeywordTemplates(c.name)];
  keywords.forEach((kw) => {
    const s = slugify(kw);
    if (!s || _seenAutoSlugs.has(s)) return;
    _seenAutoSlugs.add(s);
    autoCityPages.push({
      slug: s,
      keyword: kw,
      h1: kw.replace(/\b\w/g, (m) => m.toUpperCase()),
      title: `${kw.replace(/\b\w/g, (m) => m.toUpperCase())} | Quebec Security Services`,
      description: `${kw.replace(/\b\w/g, (m) => m.toUpperCase())} — BSP-licensed, bilingual FR/EN security in ${c.name}, Quebec. 24/7 dispatch. Request a quote in one business day.`,
      hook: `Search-term coverage for ${kw} — because that's how your prospective clients look for us.`,
      category: "auto-city",
      city_slug: c.slug,
      city_name: c.name,
      keywords: [kw, `${kw} Quebec`, `${kw} services`, `${kw} company`],
    });
  });
});

// ---- 5) Auto-generated per-service keyword pages ----
const autoServicePages = [];
_services.forEach((sv) => {
  const nativeKw = sv.keywords || [];
  const priorityCityList = ["Montreal", "Laval", "Quebec City", "Gatineau", "Longueuil", "Sherbrooke"];
  const expanded = [
    ...nativeKw,
    ...priorityCityList.map((c) => `${sv.name_en.toLowerCase()} ${c}`),
    `${sv.name_en.toLowerCase()} Quebec`,
    `${sv.name_en.toLowerCase()} near me`,
    `${sv.name_en.toLowerCase()} cost`,
    `${sv.name_en.toLowerCase()} rates`,
    `hire ${sv.name_en.toLowerCase()}`,
    `best ${sv.name_en.toLowerCase()} Quebec`,
    `24/7 ${sv.name_en.toLowerCase()}`,
  ];
  expanded.forEach((kw) => {
    const s = slugify(kw);
    if (!s || _seenAutoSlugs.has(s)) return;
    _seenAutoSlugs.add(s);
    autoServicePages.push({
      slug: s,
      keyword: kw,
      h1: kw.replace(/\b\w/g, (m) => m.toUpperCase()),
      title: `${kw.replace(/\b\w/g, (m) => m.toUpperCase())} | Quebec Security Services`,
      description: `${kw.replace(/\b\w/g, (m) => m.toUpperCase())} — BSP-licensed, bilingual FR/EN. Deployed across Quebec by Quebec Security Services. Request a quote in one business day.`,
      hook: `Search-term coverage for ${kw} — because that's how your prospective clients look for us.`,
      category: "auto-service",
      service_slug: sv.slug,
      service_name: sv.name_en,
      keywords: [kw, `${kw} Quebec`, `${kw} services`, `${kw} company`],
    });
  });
});

// ---- Combine ----
export const seoKeywords = [
  ...serviceCityCombos,
  ...intentPages,
  ...verticalPages,
  ...autoCityPages,
  ...autoServicePages,
];

// Fast lookup: build a Map for O(1) resolution
const _seoMap = new Map(seoKeywords.map((k) => [k.slug, k]));
export const getSeoBySlug = (slug) => _seoMap.get(slug);

export const seoCategories = {
  "service-city": {
    label: "Service × City combinations",
    description: "Every service, every top-tier Quebec city — targeted landing pages for search intent.",
  },
  intent: {
    label: "Intent & long-tail keywords",
    description: "Specific search phrases from 'hire a security guard' to '24-hour armed security'.",
  },
  vertical: {
    label: "Industry verticals",
    description: "Niche industries with dedicated security operating patterns — cannabis, biotech, art, wind, rail.",
  },
  "auto-city": {
    label: "City coverage (auto-generated)",
    description: "Search-term coverage for every top Quebec city — service intent and long-tail combinations.",
  },
  "auto-service": {
    label: "Service coverage (auto-generated)",
    description: "Search-term coverage for every service line — intent and modifier combinations.",
  },
};

export const seoCategoriesFr = {
  "service-city": {
    label: "Combinaisons service × ville",
    description: "Chaque service, chaque grande ville du Québec — des pages ciblées pour chaque intention de recherche.",
  },
  intent: {
    label: "Requêtes d'intention et longue traîne",
    description: "Des expressions précises, de « embaucher un agent de sécurité » à « sécurité armée 24 heures ».",
  },
  vertical: {
    label: "Secteurs d'activité",
    description: "Des industries de niche aux exigences opérationnelles propres — cannabis, biotechnologie, art, éolien, ferroviaire.",
  },
  "auto-city": {
    label: "Couverture par ville (générée)",
    description: "Couverture des requêtes pour chaque grande ville du Québec — intentions de service et combinaisons.",
  },
  "auto-service": {
    label: "Couverture par service (générée)",
    description: "Couverture des requêtes pour chaque ligne de service — intentions et modificateurs.",
  },
};

export const groupedSeo = () => {
  // Hub only shows curated categories. Auto-expanded per-city and per-service
  // pages are reachable via keyword pills on city/service detail pages instead.
  const groups = { "service-city": [], intent: [], vertical: [] };
  seoKeywords.forEach((k) => {
    if (groups[k.category]) groups[k.category].push(k);
  });
  return groups;
};
